import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import Layout from "../components/Layout";
import { SortableHeader, toggleSort, applySortFn, getPersistedSort } from "../components/SortableHeader";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { ScrollText, ChevronLeft, ChevronRight, Filter, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const IGNORED_KEYS = ["tenant_id", "created_at", "employee_id", "log_id", "avatar"];

const formatFieldName = (key) => key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const formatValue = (val, key) => {
  if (val === null || val === undefined || val === "") return "-";
  if (key === "employee_name") return val;
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
};

const ChangeDiff = ({ oldVal, newVal, action }) => {
  if (action === "delete" && oldVal) {
    return (
      <div className="space-y-1">
        {Object.entries(oldVal).filter(([k]) => !IGNORED_KEYS.includes(k)).map(([k, v]) => (
          <div key={k} className="flex items-start gap-2 text-sm">
            <span className="text-slate-500 min-w-[120px] font-medium">{formatFieldName(k)}:</span>
            <span className="text-red-600 line-through">{formatValue(v, k)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (action === "create" && newVal) {
    return (
      <div className="space-y-1">
        {Object.entries(newVal).filter(([k]) => !IGNORED_KEYS.includes(k)).map(([k, v]) => (
          <div key={k} className="flex items-start gap-2 text-sm">
            <span className="text-slate-500 min-w-[120px] font-medium">{formatFieldName(k)}:</span>
            <span className="text-emerald-700">{formatValue(v, k)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (action === "update" && newVal) {
    const changedKeys = Object.keys(newVal).filter(k => !IGNORED_KEYS.includes(k));
    if (changedKeys.length === 0) return <span className="text-xs text-slate-400">No field changes recorded</span>;
    return (
      <div className="space-y-1.5">
        {changedKeys.map(k => {
          const oldV = oldVal?.[k];
          const newV = newVal[k];
          const changed = formatValue(oldV, k) !== formatValue(newV, k);
          return (
            <div key={k} className="flex items-start gap-2 text-sm">
              <span className="text-slate-500 min-w-[120px] font-medium">{formatFieldName(k)}:</span>
              {changed ? (
                <span>
                  <span className="text-red-500 line-through mr-2">{formatValue(oldV, k)}</span>
                  <span className="text-emerald-700">{formatValue(newV, k)}</span>
                </span>
              ) : (
                <span className="text-slate-600">{formatValue(newV, k)}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return <span className="text-xs text-slate-400">No details available</span>;
};

/**
 * Given an audit log row, return `{ path, label }` for a "View" deep link,
 * or `null` when the entity has no browsable detail page. We pull the
 * employee_id out of old_value / new_value for records that live on the
 * Employee Detail page (time entries, payments, deductions, pay-rate
 * history) so a manager clicking "View" from Audit Log lands directly on
 * the right tab with the exact row scrolled into view + flashed.
 */
const getDeepLink = (log, { isSuperAdmin } = {}) => {
  if (!log) return null;
  const t = log.entity_type;
  const id = log.entity_id;
  // Merge old + new so we can pull employee_id (and other stable ids) even
  // on `update` events where `new_value` only lists the CHANGED fields.
  const val = { ...(log.old_value || {}), ...(log.new_value || {}) };
  const empId = val.employee_id || null;

  switch (t) {
    case "employee":
      return { path: `/employees/${id}`, label: "View employee" };
    case "time_entry":
      if (empId) return { path: `/employees/${empId}?tab=time&highlight=${id}`, label: "View time entry" };
      return { path: `/time-entries`, label: "View time entries" };
    case "payment":
      if (empId) return { path: `/employees/${empId}?tab=payments&highlight=${id}`, label: "View payment" };
      return { path: `/payments`, label: "View payments" };
    case "deduction":
      if (empId) return { path: `/employees/${empId}?tab=deductions&highlight=${id}`, label: "View deduction" };
      return null;
    case "pay_rate_history":
      if (empId) return { path: `/employees/${empId}?tab=pay-history&highlight=${id}`, label: "View rate change" };
      return null;
    case "pay_group":
      return { path: `/pay-groups`, label: "View pay groups" };
    case "schedule":
    case "schedule_group":
    case "availability":
      return { path: `/schedule`, label: "View schedule" };
    case "shift":
      return { path: `/shifts`, label: "View shifts" };
    case "team_invite":
    case "team_member":
      return { path: `/team`, label: "View team" };
    case "kiosk_registration_code":
    case "kiosk_device":
      return { path: `/settings?tab=timeclock`, label: "Open Time Clock settings" };
    case "tenant":
      return { path: `/settings`, label: "Open Settings" };
    case "discount_code":
      return isSuperAdmin ? { path: `/admin/discounts`, label: "View discount codes" } : null;
    case "payment_transaction": {
      // Super-admin refunds — link to the tenant detail if we know it.
      const tid = val.tenant_id || log.tenant_id;
      if (isSuperAdmin && tid) return { path: `/admin/tenants/${tid}`, label: "View tenant" };
      return null;
    }
    default:
      return null;
  }
};

const AuditLog = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [expandedLog, setExpandedLog] = useState(null);
  const [sort, setSort] = useState(() => getPersistedSort("audit_log", "timestamp", "desc"));
  const pageSize = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityFilter !== "all") params.append("entity_type", entityFilter);
      if (actionFilter !== "all") params.append("action", actionFilter);
      params.append("limit", String(pageSize));
      params.append("skip", String(page * pageSize));
      const res = await apiClient.get(`/audit-logs?${params.toString()}`);
      setLogs(res.data.audit_logs);
      setTotal(res.data.total);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [entityFilter, actionFilter, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatTimestamp = (ts) => {
    if (!ts) return "-";
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const actionColor = (action) => {
    switch (action) {
      case "create": return "bg-emerald-100 text-emerald-700";
      case "update": return "bg-blue-100 text-blue-700";
      case "delete": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const hasDetails = (log) => log.old_value || log.new_value;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">Audit Log</h1>
            <p className="text-sm text-slate-500 mt-1">Track all changes made across your workspace</p>
          </div>
          <Badge variant="outline" className="text-slate-500">{total} entries</Badge>
        </div>

        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(0); setExpandedLog(null); }}>
                <SelectTrigger className="w-[160px]" data-testid="audit-entity-filter">
                  <SelectValue placeholder="Entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="time_entry">Time Entry</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                  <SelectItem value="pay_group">Pay Group</SelectItem>
                  <SelectItem value="schedule">Schedule</SelectItem>
                  <SelectItem value="availability">Availability</SelectItem>
                </SelectContent>
              </Select>
              <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); setExpandedLog(null); }}>
                <SelectTrigger className="w-[140px]" data-testid="audit-action-filter">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold w-8"></TableHead>
                  <TableHead><SortableHeader label="Time" sortKey="timestamp" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "audit_log"))} /></TableHead>
                  <TableHead><SortableHeader label="User" sortKey="user_name" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "audit_log"))} /></TableHead>
                  <TableHead><SortableHeader label="Action" sortKey="action" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "audit_log"))} /></TableHead>
                  <TableHead><SortableHeader label="Entity" sortKey="entity_type" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "audit_log"))} /></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Entity ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold w-16 text-right">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <div className="h-10 bg-slate-100 rounded animate-pulse"></div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <ScrollText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-500">No audit logs found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  [...logs].sort(applySortFn(sort)).map((log) => {
                    const isExpanded = expandedLog === log.log_id;
                    const canExpand = hasDetails(log);
                    const deepLink = getDeepLink(log, { isSuperAdmin });
                    return (
                      <TableRow
                        key={log.log_id}
                        data-testid="audit-log-row"
                        className={`${canExpand ? "cursor-pointer hover:bg-slate-50/60" : ""} ${isExpanded ? "bg-slate-50/40" : ""}`}
                        onClick={() => canExpand && setExpandedLog(isExpanded ? null : log.log_id)}
                      >
                        <TableCell className="w-8 px-2">
                          {canExpand && (
                            isExpanded
                              ? <ChevronUp className="w-4 h-4 text-slate-400" />
                              : <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500 whitespace-nowrap">{formatTimestamp(log.timestamp)}</TableCell>
                        <TableCell className="font-medium text-[#0A192F]">{log.user_name}</TableCell>
                        <TableCell>
                          <Badge className={`${actionColor(log.action)} font-medium`}>{log.action}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-600 capitalize">{log.entity_type?.replace("_", " ")}</TableCell>
                        <TableCell>
                          <div>
                            <span className="text-xs font-mono text-slate-400">{log.entity_id}</span>
                            {log.entity_name && <span className="text-xs text-slate-600 ml-2">({log.entity_name})</span>}
                            {isExpanded && (
                              <div className="mt-3 p-3 bg-white border border-slate-200 rounded-lg" onClick={(e) => e.stopPropagation()}>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                  {log.action === "create" ? "Created Values" : log.action === "delete" ? "Deleted Values" : "Changed Fields"}
                                </p>
                                <ChangeDiff oldVal={log.old_value} newVal={log.new_value} action={log.action} />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right pr-4" onClick={(e) => e.stopPropagation()}>
                          {deepLink ? (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-[#0D9488] hover:text-[#0F766E] hover:bg-[#0D9488]/10"
                                    onClick={() => navigate(deepLink.path)}
                                    data-testid={`audit-view-btn-${log.log_id}`}
                                    aria-label={deepLink.label}
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="text-xs">{deepLink.label}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AuditLog;
